'use client'

import pluralize from 'pluralize'
import { stringifyUrl } from 'query-string'
import { ComponentProps, useMemo } from 'react'

import { TypeStyleHeadlineSmall, isUppercase } from '@syconium/little-miss-figgy'

import { GetProductRecommendationsSectionQuery } from '../../../../../__generated__/graphql/catalog/graphql'
import { trackEvent } from '../../../../../lib/analytics'
import { useNostoRecos } from '../../../../../lib/hooks/useNostoRecos'
import { useTrackProductInteraction } from '../../../../_providers/TrackingProvider.client'
import { FloatingFavoritesButton } from '../../../buttons/FavoritesButton.client'
import { isValidNostoPlacementId } from '../../../chrome/scripts/NostoScript'
import { NextLink } from '../../../navigation/NextLink'
import { PricingRange } from '../../../pricing/PricingRange.client'
import {
  ProductTileColorCount,
  ProductTileColorName,
  ProductTileColors,
  ProductTileColorsSkeleton,
  ProductTileDetails,
  ProductTileImage,
  ProductTileImageSkeleton,
  ProductTilePrice,
  ProductTilePriceSkeleton,
  ProductTileTitle,
  ProductTileTitleSkeleton,
} from '../../../tiles/ProductTile.client'
import { LIFTED_BUTTONS_DEFAULT_PIXELS, Tile, TilesLayout } from '../../../tiles/Tiles.client'
import {
  PageSection,
  PageSectionContent,
  sectionSideGutterFromMdAndExpanded,
  sectionSideGutterUntilMd,
} from '../../PageSection.client'
import { useContentfulPageSectionContext } from '../ContentfulPageSectionProvider.client'
import { ContentfulPageSectionProps } from '../ContentfulPageSections.server'

import {
  ContentfulPageCollectionSectionHeader,
  ContentfulPageCollectionSectionTitleWrapper,
} from './ContentfulPageCollectionSection.client'

type ContentfulPageProductRecommendationsSectionProps = ContentfulPageSectionProps & {
  contentfulSection: Extract<
    NonNullable<NonNullable<GetProductRecommendationsSectionQuery['pageViewSection']>>,
    { __typename: 'ProductRecommendationsSection' }
  >
  scrollBackLabel: ComponentProps<typeof TilesLayout>['scrollBackLabel']
  scrollForwardLabel: ComponentProps<typeof TilesLayout>['scrollForwardLabel']
  colorCountText: string
}

export const ContentfulPageProductRecommendationsSection = ({
  scrollBackLabel,
  scrollForwardLabel,
  contentfulSection,
  pageSectionIndex,
  colorCountText,
}: ContentfulPageProductRecommendationsSectionProps) => {
  const sectionContext = useContentfulPageSectionContext()

  const {
    nostoPlacementId: nostoPlacementIdProp,
    productCountLimit: productCountLimitProp,
    useNostoPlacementTitle,
    title: titleProp,
    hideColors,
    hidePrice,
    name: contentfulSectionName,
    titleAlignmentDesktop: titleAlignmentDesktopProp,
    titleAlignmentMobile: titleAlignmentMobileProp,
  } = contentfulSection

  const nostoPlacementId = isValidNostoPlacementId(nostoPlacementIdProp)
    ? nostoPlacementIdProp
    : 'frontpage-nosto-1'

  const titleAlignmentDesktop = titleAlignmentDesktopProp ?? 'left'
  const titleAlignmentMobile = titleAlignmentMobileProp ?? 'left'
  const productCountLimit = productCountLimitProp ?? 48

  const {
    placementTitle: nostoTitle,
    variants,
    resultId: nostoResultId,
    status,
  } = useNostoRecos({
    nostoPlacementId,
    productCountLimit,
  })

  const titleText = useNostoPlacementTitle && nostoTitle ? nostoTitle : titleProp

  const tilesPerViewportWidth = useMemo(() => {
    return {
      sm: 2,
      md: 4,
    }
  }, [])
  const tilesLayoutChoices = useMemo(() => {
    return {
      sm: 'slider',
      md: 'slider',
    } as const
  }, [])
  const tilesGutterChoices = useMemo(() => {
    return {
      sm: sectionSideGutterUntilMd,
      md: sectionSideGutterFromMdAndExpanded,
    } as const
  }, [])
  const skeletonTiles = useMemo(() => [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [])
  const liftedButtons = !hideColors || !hidePrice

  const { trackProductInteraction } = useTrackProductInteraction()

  return (
    <PageSection>
      {titleText && (
        <PageSectionContent gutterSize='expanded'>
          <ContentfulPageCollectionSectionHeader>
            <ContentfulPageCollectionSectionTitleWrapper
              $textAlign={{
                sm: titleAlignmentMobile,
                md: titleAlignmentDesktop,
              }}
            >
              <TypeStyleHeadlineSmall isUppercase={isUppercase(titleText)}>
                {titleText}
              </TypeStyleHeadlineSmall>
            </ContentfulPageCollectionSectionTitleWrapper>
          </ContentfulPageCollectionSectionHeader>
        </PageSectionContent>
      )}

      <PageSectionContent gutterSize='none'>
        <TilesLayout
          scrollBackLabel={scrollBackLabel}
          scrollForwardLabel={scrollForwardLabel}
          liftButtonsPixels={liftedButtons ? LIFTED_BUTTONS_DEFAULT_PIXELS : 0}
          layout={tilesLayoutChoices}
          visibleTiles={tilesPerViewportWidth}
          gutters={tilesGutterChoices}
        >
          {status === 'pending'
            ? skeletonTiles.map((_, index) => {
                return (
                  <Tile key={index}>
                    <ProductTileImageSkeleton aspectRatio={'slider'} />
                    <ProductTileDetails untilMdLeftPadding={tilesGutterChoices.sm + 0 === 0}>
                      <ProductTileTitleSkeleton />
                      {!hideColors ? <ProductTileColorsSkeleton /> : null}
                      {!hidePrice ? <ProductTilePriceSkeleton /> : null}
                    </ProductTileDetails>
                  </Tile>
                )
              })
            : variants.map((variant, index) => {
                const imageUrl = variant.group.images?.[0]?.url
                if (!imageUrl) return null

                const pdpUrl = stringifyUrl({
                  url: variant.urlPath,
                  query: {
                    nosto: nostoResultId,
                  },
                })

                return (
                  <Tile
                    key={`${variant.id}${index}`}
                    {...trackEvent({
                      category: 'product-carousel-tile-nosto',
                      customAttributes: {
                        'data-heap-placement-id': nostoPlacementId,
                        'data-heap-parent-title': titleText ?? undefined,
                        'data-heap-section-name': contentfulSectionName ?? undefined,
                        'data-heap-contentful-name': contentfulSectionName ?? undefined,
                      },
                      action: 'click tile',
                      label: `tile-${index + 1}`,
                      value: variant.group.title,
                      pageSectionIndex: pageSectionIndex,
                      pageSectionName: sectionContext.pageSectionAnalyticsName,
                      correspondingAsset: imageUrl,
                    })}
                  >
                    <NextLink
                      href={pdpUrl}
                      onClick={() => {
                        trackProductInteraction({
                          handle: variant.group.product.handle,
                          color: variant.group.color?.id ?? '',
                          id: variant.group.id,
                          shopifyId: variant.shopifyProductId,
                        })
                      }}
                    >
                      <ProductTileImage
                        alt={variant.group.title}
                        loading='lazy'
                        src={imageUrl}
                        aspectRatio={'slider'}
                        widths={{
                          unit: 'vw',
                          sm: 100 / tilesPerViewportWidth.sm,
                          md: 100 / tilesPerViewportWidth.md,
                        }}
                      />
                      <ProductTileDetails untilMdLeftPadding={tilesGutterChoices.sm + 0 === 0}>
                        <ProductTileTitle>{variant.group.title}</ProductTileTitle>
                        {!hideColors ? (
                          <ProductTileColors>
                            {variant.group.color?.id !== 'No Color' && (
                              <ProductTileColorName>
                                {variant.group.color?.displayValue}
                              </ProductTileColorName>
                            )}
                            <ProductTileColorCount>
                              {variant.group.product.options.colorsTotalCount}{' '}
                              {pluralize(
                                colorCountText,
                                variant.group.product.options.colorsTotalCount
                              )}
                            </ProductTileColorCount>
                          </ProductTileColors>
                        ) : null}
                        {!hidePrice && variant.pricing ? (
                          <ProductTilePrice>
                            <PricingRange {...variant.pricing} />
                          </ProductTilePrice>
                        ) : null}
                      </ProductTileDetails>
                    </NextLink>
                    {variant.group.color?.id &&
                      variant.fit?.id &&
                      variant.group.product.__typename === 'ProductCatalogStandardProduct' && (
                        <FloatingFavoritesButton
                          productGroupHandle={variant.group.product.handle}
                          colorKey={variant.group.color.id}
                          fitKey={variant.fit.id}
                          sizeKey={null}
                          productGroupImageSrc={imageUrl}
                        />
                      )}
                  </Tile>
                )
              })}
        </TilesLayout>
      </PageSectionContent>
    </PageSection>
  )
}
